import { cn } from '@/lib/utils';
import type { ReactNode } from 'react';

/**
 * The shape of ONE filter control, and the two states its border can be in
 * (maquette `Main.dc.html` / `ListeFiltree.dc.html`).
 *
 * Fourteen controls share it - five in the always-visible row, four in the
 * « Plus de filtres » panel, four checkboxes and the sort - so it is declared
 * once. Written per call site, the fourteen would drift, and the ones that
 * drifted would be the ones behind the panel that nobody opens.
 *
 * ## The border says « this one is armed »
 *
 * A neutral control is `gray-200` with black ink; an armed one is `bleu-nuit`
 * with `bleu-nuit` ink at 600. That pair is the maquette's, and it is the only
 * signal a `<select>` can carry without a chip: the chips below say WHICH
 * filters are on, the border says which control to reach for to change one.
 * `bleu-nuit` because the charte gives this application one accent and this is
 * it - the noyaux stay for the two pointers, which mean something else.
 */

/** The ground, the metrics and the focus ring, without the border colour. */
const CONTROL_BASE =
  'h-9.5 rounded-md border-1 bg-white px-3 text-sm leading-5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1';

export function filterControlClass(armed: boolean, className?: string): string {
  return cn(
    CONTROL_BASE,
    armed ? 'border-bleu-nuit font-semibold text-bleu-nuit' : 'border-gray-200 text-gray-800',
    className,
  );
}

export interface FilterFieldProps {
  label: string;
  /** The control's own id - the label is a real `<label for>` and never a bare span. */
  htmlFor: string;
  children: ReactNode;
  className?: string;
}

export function FilterField({ label, htmlFor, children, className }: FilterFieldProps) {
  return (
    <div className={cn('flex max-w-full flex-col gap-1', className)}>
      <label
        htmlFor={htmlFor}
        className="font-semibold text-gray-600 text-xs uppercase tracking-wide"
      >
        {label}
      </label>
      {children}
    </div>
  );
}
